import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { dashboardApi, kpiApi, ApiError } from "@/api";
import type { DashboardSummary, DashboardDeadlineRow, KpiHistoryPoint } from "@/types";
import { formatDateTime, formatRelative } from "@/lib/format";
import {
  readDashboardCache,
  writeDashboardCache,
  isDashboardSnapshotStale,
  DASHBOARD_CACHE_TTL_MS,
  type DashboardSnapshot,
} from "@/lib/dashboardCache";
import "./studio-dashboard.css";

/* ─── formatting ──────────────────────────────────────────────────────────── */

function gbp(n: number): string {
  const v = n ?? 0;
  const whole = Number.isInteger(v);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(v);
}
/** Compact £k for dense bar labels (e.g. £14.2k). */
function gbpK(n: number): string {
  return "£" + ((n ?? 0) / 1000).toFixed(1) + "k";
}
/** month_start is UTC-midnight first-of-month — read it in UTC so the label
 *  doesn't slip to the previous month in a negative timezone. */
function shortMonth(ms: number): string {
  return new Date(ms).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
}
function monthYear(ms: number): string {
  return new Date(ms).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}
function prevMonthName(ms: number): string {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth() - 1, 1).toLocaleDateString("en-GB", { month: "long" });
}
function sliceLast<T>(arr: T[], n: number): T[] {
  return n >= arr.length ? arr : arr.slice(arr.length - n);
}
/** k short month labels ending at `nowMs`, oldest-first — only used as a
 *  fallback axis for the sample charts if the live invoiced series is empty. */
function monthsBack(nowMs: number, k: number): string[] {
  const d = new Date(nowMs);
  const out: string[] = [];
  for (let i = k - 1; i >= 0; i--) {
    out.push(new Date(d.getFullYear(), d.getMonth() - i, 1).toLocaleDateString("en-GB", { month: "short" }));
  }
  return out;
}

/* ─── sample dataset (defensive fallback) ─────────────────────────────────────
 * Leads & quotes are now live: the API returns those blocks with
 * placeholder:false + real arrays, so these widgets render real data. This
 * dataset is only used if a block ever comes back with placeholder !== false
 * (e.g. the backend degrades), in which case the card shows a "Sample" tag. */

const SAMPLE = {
  newLeads: { value: 26, delta_pct: 18 },
  quotesSent: { value: 11, win_rate: 36 },
  funnel: [
    { label: "Leads", count: 142 },
    { label: "Feasibility", count: 64 },
    { label: "Quote sent", count: 57 },
    { label: "Won", count: 21 },
  ],
  sources: [
    { source: "WhatConverts", leads: 82, won: 11 },
    { source: "cal.com", leads: 38, won: 6 },
    { source: "Referral", leads: 22, won: 4 },
  ],
  leadsMonthly: [17, 19, 23, 18, 21, 25, 20, 24, 22, 28, 24, 26],
  leadsThisMonth: 26,
  leadsYtd: 142,
  quotesMonthly: [6, 7, 9, 8, 10, 9, 8, 11, 9, 12, 10, 11],
  quotesWin: [31, 30, 34, 31, 36, 33, 35, 38, 34, 40, 35, 36],
  quotesThisMonth: 11,
  quotesYtd: 57,
  quotesWinRate: 36,
};

const FUNNEL_COLORS = ["var(--stone-200)", "var(--stone-300)", "var(--stone-400)", "var(--stone-600)"];
const SOURCE_COLORS = ["var(--slate)", "var(--stone-400)", "var(--paper-300)", "var(--stone-600)"];

/* ─── range selector → trend_months ───────────────────────────────────────── */

type Range = "month" | "ytd" | "year";
const RANGES: { value: Range; label: string }[] = [
  { value: "month", label: "This month" },
  { value: "ytd", label: "YTD" },
  { value: "year", label: "Last 12 months" },
];
function trendMonthsFor(range: Range): number {
  if (range === "month") return 1;
  if (range === "year") return 12;
  return new Date().getMonth() + 1; // YTD: Jan…current
}
function isRange(v: unknown): v is Range {
  return RANGES.some((r) => r.value === v);
}

/* ─── page ────────────────────────────────────────────────────────────────── */

/** The page paints from the localStorage snapshot (lib/dashboardCache) when
 *  one exists, so a launch is instant; it re-fetches only when that copy is
 *  older than the TTL, was taken for a different trend range, or staff press
 *  Refresh. The header shows when the figures were last fetched. A snapshot
 *  left open past the TTL refreshes itself the next time the tab is visible.
 *  A failed background refresh keeps the snapshot on screen with a notice
 *  rather than blanking the page. */
export function StudioDashboard() {
  const [snap, setSnap] = useState<DashboardSnapshot | null>(readDashboardCache);
  const [range, setRange] = useState<Range>(() => (snap && isRange(snap.range) ? snap.range : "year"));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Ticks each minute (and on tab focus) so "Updated … ago" stays honest and
  // the staleness check re-runs without anyone touching the page.
  const [now, setNow] = useState(() => Date.now());
  // Only the most recent request may apply: a Refresh fired mid range-switch
  // (or a fast double-click) must not be overwritten by the earlier response.
  const requestSeq = useRef(0);
  // Synchronous twin of `refreshing`: the fetch effect reads it so a re-run in
  // the same render pass (StrictMode's double mount, a tick landing mid-fetch)
  // cannot start a second request before the state update has committed.
  const inFlight = useRef(false);
  const snapRef = useRef(snap);
  snapRef.current = snap;

  const load = useCallback((r: Range) => {
    const seq = ++requestSeq.current;
    const trendMonths = trendMonthsFor(r);
    inFlight.current = true;
    setRefreshing(true);
    Promise.all([
      dashboardApi.getDashboardSummary({ trend_months: trendMonths }),
      // Trend is non-critical: on failure keep whatever series we already had.
      kpiApi.getKpiHistory("order_book").then((res) => res.points ?? []).catch(() => null),
    ])
      .then(([summary, points]) => {
        if (seq !== requestSeq.current) return;
        const next: DashboardSnapshot = {
          savedAt: Date.now(),
          range: r,
          trendMonths,
          summary,
          orderBookPoints: points ?? snapRef.current?.orderBookPoints ?? [],
        };
        writeDashboardCache(next);
        setSnap(next);
        setNow(next.savedAt);
        setError(undefined);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setError(err instanceof ApiError ? err.message : "Could not load the dashboard.");
      })
      .finally(() => { if (seq === requestSeq.current) { inFlight.current = false; setRefreshing(false); } });
  }, []);

  // Single fetch trigger. Nothing to show → fetch. Snapshot for another range,
  // a YTD taken last month, or older than the TTL → refresh in the background,
  // but only while the tab is visible (a hidden tab waits until it's looked at)
  // and not after a failure (the notice offers a retry instead of hammering).
  useEffect(() => {
    if (inFlight.current || refreshing || error) return;
    if (!snap) { load(range); return; }
    if (document.visibilityState !== "visible") return;
    const mismatched = snap.range !== range || snap.trendMonths !== trendMonthsFor(range);
    if (mismatched || isDashboardSnapshotStale(snap, now)) load(range);
  }, [snap, range, refreshing, error, now, load]);

  useEffect(() => {
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  function refresh() { if (!refreshing) load(range); }
  // A failed refresh parks the fetch effect; switching range should still fetch.
  function changeRange(r: Range) { setError(undefined); setRange(r); }

  const data = snap?.summary ?? null;
  return (
    <section className="sd">
      <Header
        periodLabel={data ? `${monthYear(data.period?.now || data.generated_at)} · vs ${prevMonthName(data.period?.now || data.generated_at)}` : ""}
        range={range}
        onRange={changeRange}
        onRefresh={refresh}
        refreshing={refreshing}
        updatedAt={snap?.savedAt ?? null}
      />

      {snap && error && (
        <p className="sd-error" role="alert">
          Couldn’t refresh — {error.replace(/\.\s*$/, "")}. Showing figures from {formatDateTime(snap.savedAt)}.{" "}
          <button type="button" className="sd-error__retry" onClick={refresh}>Try again</button>
        </p>
      )}

      {snap ? (
        <Grid data={snap.summary} orderBookPoints={snap.orderBookPoints} />
      ) : error ? (
        <p className="sd-error" role="alert">
          {error}{" "}
          <button type="button" className="sd-error__retry" onClick={refresh}>Retry</button>
        </p>
      ) : (
        <Skeleton />
      )}
    </section>
  );
}

const CACHE_HOURS = Math.round(DASHBOARD_CACHE_TTL_MS / 3_600_000);

function Header({
  periodLabel,
  range,
  onRange,
  onRefresh,
  refreshing,
  updatedAt,
}: {
  periodLabel: string;
  range: Range;
  onRange: (r: Range) => void;
  onRefresh: () => void;
  refreshing: boolean;
  /** Epoch-ms of the snapshot on screen; null while the first load is in flight.
   *  The page re-renders on a minute tick, which is what keeps the relative
   *  label ("Updated 3 minutes ago") moving. */
  updatedAt: number | null;
}) {
  return (
    <header className="sd-head">
      <div>
        <h1>Studio overview</h1>
        <p className="sd-head__period">{periodLabel || " "}</p>
      </div>
      <div className="sd-head__controls">
        <div className="sd-seg" role="group" aria-label="Trend range">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              className={`sd-seg__btn${range === r.value ? " is-on" : ""}`}
              onClick={() => onRange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
        {updatedAt != null && (
          <span
            className="sd-head__updated"
            title={`Fetched ${formatDateTime(updatedAt)} · figures are kept for ${CACHE_HOURS} hours, then refreshed automatically`}
          >
            {refreshing ? "Refreshing…" : `Updated ${formatRelative(updatedAt)}`}
          </span>
        )}
        <button
          type="button"
          className={`sd-refresh${refreshing ? " is-spinning" : ""}`}
          onClick={onRefresh}
          disabled={refreshing}
          aria-label="Refresh now"
          title="Refresh now"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
        </button>
      </div>
    </header>
  );
}

/* ─── grid ────────────────────────────────────────────────────────────────── */

function Grid({ data, orderBookPoints }: { data: DashboardSummary; orderBookPoints: KpiHistoryPoint[] }) {
  const now = data.period?.now || data.generated_at || 0;
  const prevMonth = now ? prevMonthName(now) : "last month";
  const hero = data.hero;
  const leadsSample = data.leads?.placeholder !== false; // default to sample when flag absent
  const quotesSample = data.quotes?.placeholder !== false;

  // Shared month axis from the live invoiced series (length = trend_months).
  const invMonthly = data.invoices?.monthly_invoiced ?? [];
  const n = invMonthly.length || 12;
  const months = invMonthly.length ? invMonthly.map((m) => shortMonth(m.month_start)) : monthsBack(now || Date.now(), n);

  return (
    <div className="sd-grid" key={`${months.length}`}>
      {/* Hero KPI row */}
      <div className="sd-kpis">
        <KpiCard
          label="New leads"
          value={String(hero?.new_leads?.placeholder !== false ? SAMPLE.newLeads.value : hero.new_leads.value)}
          sub={<Delta pct={hero?.new_leads?.placeholder !== false ? SAMPLE.newLeads.delta_pct : hero.new_leads.delta_pct} />}
          foot={`vs ${prevMonth}`}
          sample={hero?.new_leads?.placeholder !== false}
        />
        <KpiCard
          label="Quotes sent"
          value={String(hero?.quotes_sent?.placeholder !== false ? SAMPLE.quotesSent.value : hero.quotes_sent.value)}
          sub={<span className="sd-kpi__stat">{(hero?.quotes_sent?.placeholder !== false ? SAMPLE.quotesSent.win_rate : hero.quotes_sent.win_rate)}% win rate</span>}
          foot="this month"
          sample={hero?.quotes_sent?.placeholder !== false}
        />
        <KpiCard
          label="Active jobs"
          value={String(hero?.active_jobs?.value ?? 0)}
          sub={<Delta abs={hero?.active_jobs?.delta_abs ?? 0} />}
          foot={`vs ${prevMonth}`}
        />
        <KpiCard
          label="Invoiced"
          value={gbp(hero?.invoiced?.value ?? 0)}
          sub={<Delta pct={hero?.invoiced?.delta_pct ?? 0} />}
          foot={`vs ${prevMonth}`}
        />
        <KpiCard
          label="Outstanding"
          value={gbp(hero?.outstanding?.value ?? 0)}
          sub={<span className="badge">{hero?.outstanding?.count ?? 0} invoices</span>}
          foot={`${(data.invoices?.aged_receivables ?? []).length} age bands`}
        />
        <OrderBookKpi value={hero?.order_book?.value ?? 0} jobs={hero?.order_book?.jobs ?? 0} points={orderBookPoints} />
      </div>

      {/* Funnel | Lead sources */}
      <div className="sd-row sd-row--lf">
        <Card title="Pipeline funnel" subtitle="Stage-to-stage conversion" sample={leadsSample}>
          <Funnel
            stages={leadsSample ? SAMPLE.funnel : (data.leads?.funnel ?? []).map((f) => ({ label: f.label, count: f.count }))}
          />
        </Card>
        <Card title="Lead sources" sample={leadsSample}>
          <Sources
            sources={
              leadsSample
                ? SAMPLE.sources.map((s) => ({ name: s.source, leads: s.leads, won: s.won, conv: Math.round((s.won / Math.max(1, s.leads)) * 100) }))
                : (data.leads?.sources ?? []).map((s) => ({ name: s.source, leads: s.leads, won: s.won, conv: Math.round(s.conversion_pct) }))
            }
          />
        </Card>
      </div>

      {/* New leads | Quotes combo */}
      <div className="sd-row sd-row--even">
        <Card
          title="New leads"
          aside={<span className="sd-readout">{leadsSample ? `${SAMPLE.leadsThisMonth} this month · ${SAMPLE.leadsYtd} YTD` : `${data.leads?.this_month ?? 0} this month · ${data.leads?.ytd ?? 0} YTD`}</span>}
          sample={leadsSample}
        >
          <Bars
            bars={(leadsSample
              ? sliceLast(SAMPLE.leadsMonthly, n).map((v, i, a) => ({ value: String(v), label: months[months.length - a.length + i] ?? "", h: v }))
              : (data.leads?.monthly ?? []).map((m) => ({ value: String(m.count), label: shortMonth(m.month), h: m.count }))
            )}
          />
        </Card>
        <Card
          title="Quotes sent & win rate"
          legend={<><LegendSwatch color="var(--stone-400)" label="Quotes sent" /><LegendLine label="Win rate %" /></>}
          aside={<span className="sd-readout">{quotesSample ? `${SAMPLE.quotesThisMonth} this month · ${SAMPLE.quotesYtd} YTD · ${SAMPLE.quotesWinRate}% win` : `${data.quotes?.this_month ?? 0} this month · ${data.quotes?.ytd ?? 0} YTD · ${data.quotes?.win_rate ?? 0}% win`}</span>}
          sample={quotesSample}
        >
          <QuotesCombo
            bars={
              quotesSample
                ? sliceLast(SAMPLE.quotesMonthly, n).map((v, i, a) => ({ value: String(v), label: months[months.length - a.length + i] ?? "", h: v }))
                : (data.quotes?.monthly ?? []).map((m) => ({ value: String(m.sent), label: shortMonth(m.month), h: m.sent }))
            }
            win={quotesSample ? sliceLast(SAMPLE.quotesWin, n) : (data.quotes?.monthly ?? []).map((m) => m.win_rate)}
          />
        </Card>
      </div>

      {/* Active jobs by stage | Deadline tracker */}
      <div className="sd-row sd-row--jd">
        <Card title="Active jobs by stage" aside={<span className="sd-readout">{data.jobs?.active_total ?? 0} live</span>}>
          <StageBars rows={(data.jobs?.by_stage ?? []).map((j) => ({ label: j.label, count: j.count }))} />
        </Card>
        <Card
          title="Statutory deadline tracker"
          subtitle="Live applications by determination date"
        >
          <Deadlines rows={data.applications?.deadline_tracker ?? []} />
        </Card>
      </div>

      {/* Monthly invoiced | Aged receivables */}
      <div className="sd-row sd-row--even">
        <Card title="Monthly invoiced" aside={<span className="sd-readout">{gbp(data.invoices?.invoiced_this_month ?? 0)} this month · {gbp(invMonthly.reduce((s, m) => s + m.value, 0))} total</span>}>
          <Bars
            bars={invMonthly.map((m) => ({ value: gbpK(m.value), label: shortMonth(m.month_start), h: m.value }))}
            compact
          />
        </Card>
        <Card title="Aged receivables" aside={<span className="sd-readout">{gbp(data.invoices?.outstanding_total ?? 0)} outstanding</span>}>
          <Aged bands={data.invoices?.aged_receivables ?? []} />
        </Card>
      </div>

      {/* Tasks by priority */}
      <Card title="Open tasks by priority" aside={(data.tasks?.overdue_count ?? 0) > 0 ? <span className="badge badge-warning">{data.tasks.overdue_count} overdue</span> : undefined}>
        <TaskTiles by={data.tasks?.by_priority ?? []} />
      </Card>
    </div>
  );
}

/* ─── shells ──────────────────────────────────────────────────────────────── */

function Card({
  title,
  subtitle,
  aside,
  legend,
  sample,
  children,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  legend?: ReactNode;
  sample?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`sd-card${sample ? " sd-card--sample" : ""}`}>
      <div className="sd-card__head">
        <div>
          <div className="sd-card__title">{title}</div>
          {subtitle ? <div className="sd-card__sub">{subtitle}</div> : null}
          {legend ? <div className="sd-legend">{legend}</div> : null}
        </div>
        <div className="sd-card__aside">
          {aside}
          {sample ? <span className="sd-sample-tag">Sample data</span> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

function KpiCard({ label, value, sub, foot, sample, chart }: { label: string; value: string; sub: ReactNode; foot: string; sample?: boolean; chart?: ReactNode }) {
  return (
    <div className={`sd-kpi${sample ? " sd-kpi--sample" : ""}`}>
      <div className="sd-kpi__head">
        <span className="sd-kpi__label">{label}</span>
        {sample ? <span className="sd-sample-tag">Sample</span> : null}
      </div>
      <div className="sd-kpi__value numeric">{value}</div>
      {chart ? <div className="sd-kpi__spark">{chart}</div> : null}
      <div className="sd-kpi__foot">
        {sub}
        <span className="sd-kpi__foottext">{foot}</span>
      </div>
    </div>
  );
}

/** Order book KPI card with a trend sparkline + "vs 30 days ago" delta, fed by
 *  the kpi_snapshot daily history. The headline is the live value from the
 *  summary; the spark + delta accrue as the overnight snapshots build up — a
 *  brand-new history shows just the headline + jobs count until a few days are
 *  banked, then the trend appears. Today's point uses the live value (not the
 *  overnight snapshot) so intra-day moves show. The series is fetched (and
 *  cached) by the page alongside the summary; if that fetch failed, `points`
 *  is whatever was last cached, or empty — the headline still renders. */
function OrderBookKpi({ value, jobs, points }: { value: number; jobs: number; points: KpiHistoryPoint[] }) {
  const merged = mergeLiveToday(points, value);
  const series = merged.map((p) => p.value);
  const deltaPct = deltaVsDaysAgo(merged, 30);
  const jobsLabel = `${jobs} ${jobs === 1 ? "job" : "jobs"}`;
  return (
    <KpiCard
      label="Order book"
      value={gbp(Math.round(value))}
      sub={deltaPct != null ? <Delta pct={deltaPct} /> : <span className="badge">{jobsLabel}</span>}
      foot={deltaPct != null ? `uninvoiced fees · ${jobsLabel}` : "uninvoiced fees"}
      chart={series.length >= 2 ? <Sparkline points={series} /> : undefined}
    />
  );
}

/** The snapshot series is written overnight, so its newest point lags the live
 *  figure all day. Splice the live value in as today's point — replacing today's
 *  snapshot if one already exists, else appending — so the sparkline + delta
 *  reflect intra-day movement. kpi/order_book is the same calc the snapshots use,
 *  so on a quiet day live == today's snapshot and the trend stays flat. */
function mergeLiveToday(points: KpiHistoryPoint[], liveValue: number): KpiHistoryPoint[] {
  const today = new Date().toISOString().slice(0, 10);
  const last = points[points.length - 1];
  if (last && last.date === today) return [...points.slice(0, -1), { ...last, value: liveValue }];
  return [...points, { date: today, value: liveValue }];
}

/** % change between the latest snapshot and the one closest to `days` ago
 *  (falling back to the earliest point we hold). null when no meaningful delta
 *  is computable yet — fewer than two points, or a zero baseline. */
function deltaVsDaysAgo(points: KpiHistoryPoint[], days: number): number | null {
  if (points.length < 2) return null;
  const latest = points[points.length - 1];
  const cutoff = Date.parse(`${latest.date}T00:00:00Z`) - days * 86400000;
  // Points are oldest-first: take the newest point at/under the cutoff, else
  // the oldest point available (so a <30-day history still shows a trend).
  let baseline = points[0];
  for (const p of points) {
    if (Date.parse(`${p.date}T00:00:00Z`) <= cutoff) baseline = p;
  }
  if (baseline === latest || baseline.value === 0) return null;
  return Math.round(((latest.value - baseline.value) / baseline.value) * 100);
}

/** Tiny inline trend line — pure SVG, stretched to the card width with a
 *  non-scaling stroke so it stays crisp. Matches the dashboard's hand-rolled
 *  chart style (no chart library). */
function Sparkline({ points }: { points: number[] }) {
  const w = 100;
  const h = 26;
  const pad = 3;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const line = points
    .map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg className="sd-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={line} fill="none" stroke="var(--slate)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Delta({ pct, abs }: { pct?: number; abs?: number }) {
  const v = pct ?? abs ?? 0;
  const up = v > 0;
  const text = pct != null ? `${pct > 0 ? "+" : ""}${pct}%` : `${abs! > 0 ? "+" : ""}${abs}`;
  if (v === 0) return <span className="sd-delta sd-delta--flat">{text}</span>;
  return (
    <span className={`sd-delta ${up ? "sd-delta--up" : "sd-delta--down"}`}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {up ? <><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></> : <><path d="M12 5v14" /><path d="M19 12l-7 7-7-7" /></>}
      </svg>
      {text}
    </span>
  );
}

/* ─── charts ──────────────────────────────────────────────────────────────── */

function Funnel({ stages }: { stages: { label: string; count: number }[] }) {
  if (stages.length === 0) return <Empty label="No pipeline yet" />;
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="sd-funnel">
      {stages.map((s, i) => {
        const conv = i > 0 && stages[i - 1].count > 0 ? Math.round((s.count / stages[i - 1].count) * 100) + "%" : null;
        return (
          <div key={s.label} className="sd-funnel__row">
            <div className="sd-funnel__top">
              <span className="sd-funnel__label">{s.label}</span>
              <span className="sd-funnel__nums">
                <span className="numeric sd-funnel__count">{s.count}</span>
                {conv ? <span className="numeric sd-funnel__conv">{conv}</span> : null}
              </span>
            </div>
            <div className="sd-funnel__track">
              <div className="sd-funnel__fill" style={{ width: `${Math.max(7, Math.round((s.count / max) * 100))}%`, background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Sources({ sources }: { sources: { name: string; leads: number; won: number; conv: number }[] }) {
  if (sources.length === 0) return <Empty label="No lead sources yet" />;
  const total = sources.reduce((s, c) => s + c.leads, 0);
  let acc = 0;
  const segs = sources.map((c, i) => {
    const from = total > 0 ? (acc / total) * 100 : 0;
    acc += c.leads;
    const to = total > 0 ? (acc / total) * 100 : 0;
    return `${SOURCE_COLORS[i % SOURCE_COLORS.length]} ${from}% ${to}%`;
  });
  return (
    <div className="sd-sources">
      <div className="sd-donut" style={{ background: `conic-gradient(${segs.join(", ")})` }}>
        <div className="sd-donut__hole">
          <span className="sd-donut__total numeric">{total}</span>
          <span className="sd-donut__cap">leads</span>
        </div>
      </div>
      <div className="sd-sources__table">
        <div className="sd-sources__hrow">
          <span className="sd-sources__ch">Channel</span>
          <span className="sd-sources__n">Leads</span>
          <span className="sd-sources__n">Won</span>
          <span className="sd-sources__n">Conv.</span>
        </div>
        {sources.map((c, i) => (
          <div key={c.name} className="sd-sources__row">
            <span className="sd-sources__ch"><span className="sd-dot" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />{c.name}</span>
            <span className="sd-sources__n numeric">{c.leads}</span>
            <span className="sd-sources__n numeric">{c.won}</span>
            <span className="sd-sources__n sd-sources__n--strong numeric">{c.conv}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bars({ bars, compact }: { bars: { value: string; label: string; h: number }[]; compact?: boolean }) {
  if (bars.length === 0) return <Empty label="No data for this period" />;
  const max = Math.max(1, ...bars.map((b) => b.h));
  return (
    <div className="sd-bars">
      {bars.map((b, i) => (
        <div key={i} className="sd-bars__col">
          <span className={`sd-bars__val${compact ? " sd-bars__val--sm" : ""} numeric`}>{b.value}</span>
          <div className="sd-bars__track">
            <div className="sd-bars__fill" style={{ height: `${Math.max(4, Math.round((b.h / max) * 100))}%`, background: i === bars.length - 1 ? "var(--slate)" : "var(--stone-300)" }} title={`${b.label}: ${b.value}`} />
          </div>
          <span className="sd-bars__month">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

function QuotesCombo({ bars, win }: { bars: { value: string; label: string; h: number }[]; win: number[] }) {
  if (bars.length === 0) return <Empty label="No quotes for this period" />;
  const max = Math.max(1, ...bars.map((b) => b.h));
  const winMax = Math.max(50, ...win);
  const n = win.length;
  // Map a win-rate % to a vertical position (top %) and a series index to a
  // horizontal position (centred over its bar), so the line, the markers and
  // the right-hand axis all share one coordinate system.
  const yOf = (v: number) => 96 - (v / winMax) * 90;
  const xOf = (i: number) => (n > 1 ? ((i + 0.5) / n) * 100 : 50);
  // Only plot win-rate for months that actually sent quotes — a month with 0
  // sent has no real win rate (its 0% is "no data", not a loss), so drawing it
  // as a dip to 0% is misleading. Empty months simply get no marker/segment.
  const pointsData = win.map((r, i) => ({ i, r })).filter(({ i }) => (bars[i]?.h ?? 0) > 0);
  const pts = pointsData.map(({ i, r }) => `${xOf(i).toFixed(1)},${yOf(r).toFixed(1)}`).join(" ");
  const ticks = [winMax, Math.round(winMax / 2), 0];
  return (
    <div className="sd-bars sd-bars--combo">
      {/* win-rate gridlines — faint solid, so the dashed line reads as the data */}
      <div className="sd-bars__plot" aria-hidden="true">
        {ticks.slice(0, 2).map((t) => (
          <div key={t} className="sd-bars__grid" style={{ top: `${yOf(t)}%` }} />
        ))}
      </div>
      {bars.map((b, i) => (
        <div key={i} className="sd-bars__col">
          <span className="sd-bars__val numeric">{b.value}</span>
          <div className="sd-bars__track">
            <div className="sd-bars__fill" style={{ height: `${Math.max(4, Math.round((b.h / max) * 100))}%`, background: "var(--stone-400)" }} title={`${b.label}: ${b.value} sent · ${win[i] ?? "–"}% won`} />
          </div>
          <span className="sd-bars__month">{b.label}</span>
        </div>
      ))}
      <svg className="sd-bars__line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <polyline points={pts} fill="none" stroke="var(--slate)" strokeWidth="1.4" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* win-rate markers — value label reveals on hover (the right axis gives the scale) */}
      <div className="sd-bars__plot">
        {pointsData.map(({ i, r }) => (
          <span key={i} className="sd-bars__dot" style={{ left: `${xOf(i)}%`, top: `${yOf(r)}%` }}>
            <span className="sd-bars__dotval">{r}%</span>
          </span>
        ))}
      </div>
      {/* win-rate scale (right axis) */}
      <div className="sd-bars__yaxis" aria-hidden="true">
        {ticks.map((t) => (
          <span key={t} className="sd-bars__tick" style={{ top: `${yOf(t)}%` }}>{t}%</span>
        ))}
      </div>
    </div>
  );
}

function StageBars({ rows }: { rows: { label: string; count: number }[] }) {
  if (rows.length === 0) return <Empty label="No active jobs" />;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="sd-stages">
      {rows.map((r) => (
        <div key={r.label} className="sd-stages__row">
          <span className="sd-stages__label">{r.label}</span>
          <div className="sd-stages__track">
            <div className="sd-stages__fill" style={{ width: `${Math.max(6, Math.round((r.count / max) * 100))}%` }} />
          </div>
          <span className="sd-stages__count numeric">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

function Deadlines({ rows }: { rows: DashboardDeadlineRow[] }) {
  if (rows.length === 0) {
    return (
      <Empty
        label="No upcoming deadlines"
        hint="Statutory determination dates appear here once applications carry them."
      />
    );
  }
  return (
    <div className="sd-deadlines">
      {rows.map((d) => {
        const overdue = d.rag === "overdue" || d.days_to_decision < 0;
        const warn = d.rag === "warning";
        const cls = overdue ? "badge badge-danger" : warn ? "badge badge-warning" : "badge";
        const pill = overdue ? `${Math.abs(d.days_to_decision)}d overdue` : `${d.days_to_decision}d left`;
        const bar = overdue ? "var(--danger)" : warn ? "var(--warning)" : "var(--paper-300)";
        return (
          <div key={d.application_id} className="sd-deadlines__row" style={{ borderLeftColor: bar, background: overdue ? "var(--danger-bg)" : "transparent" }}>
            <div className="sd-deadlines__main">
              <div className="sd-deadlines__ref numeric">{d.app_reference}</div>
              <div className="sd-deadlines__meta">{d.authority} · {new Date(d.date_statutory_deadline).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}</div>
            </div>
            <span className={cls}>{pill}</span>
          </div>
        );
      })}
    </div>
  );
}

function Aged({ bands }: { bands: { band: string; amount: number }[] }) {
  const LABELS: Record<string, string> = { "0_30": "0–30 days", "31_60": "31–60 days", "61_90": "61–90 days", "90_plus": "90+ days" };
  const COLORS: Record<string, { bar: string; text: string }> = {
    "0_30": { bar: "var(--stone-200)", text: "var(--slate)" },
    "31_60": { bar: "var(--stone-400)", text: "var(--slate)" },
    "61_90": { bar: "var(--warning)", text: "var(--warning)" },
    "90_plus": { bar: "var(--danger)", text: "var(--danger)" },
  };
  const total = bands.reduce((s, b) => s + b.amount, 0);
  if (total === 0) return <Empty label="Nothing outstanding" hint="All invoices are settled." />;
  const max = Math.max(1, ...bands.map((b) => b.amount));
  return (
    <div className="sd-aged">
      {bands.map((b) => {
        const c = COLORS[b.band] ?? { bar: "var(--stone-300)", text: "var(--slate)" };
        return (
          <div key={b.band} className="sd-aged__row">
            <div className="sd-aged__top">
              <span className="sd-aged__band">{LABELS[b.band] ?? b.band}</span>
              <span className="sd-aged__amt numeric" style={{ color: c.text }}>{gbp(b.amount)}</span>
            </div>
            <div className="sd-aged__track">
              <div className="sd-aged__fill" style={{ width: `${Math.max(b.amount > 0 ? 6 : 0, Math.round((b.amount / max) * 100))}%`, background: c.bar }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TaskTiles({ by }: { by: { priority: string; count: number }[] }) {
  const ORDER = ["urgent", "high", "medium", "low"];
  const TONE: Record<string, string> = { urgent: "danger", high: "warning", medium: "neutral", low: "neutral" };
  const sorted = [...by].sort((a, b) => ORDER.indexOf(a.priority) - ORDER.indexOf(b.priority));
  if (sorted.length === 0) return <Empty label="No open tasks" />;
  return (
    <div className="sd-tasks">
      {sorted.map((t) => (
        <div key={t.priority} className={`sd-task sd-task--${TONE[t.priority] ?? "neutral"}`}>
          <div className="sd-task__count numeric">{t.count}</div>
          <div className="sd-task__row">
            <span className="sd-task__label">{t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}</span>
            <span className="sd-task__cap">open</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── little bits ─────────────────────────────────────────────────────────── */

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return <span className="sd-legend__item"><span className="sd-legend__sw" style={{ background: color }} />{label}</span>;
}
function LegendLine({ label }: { label: string }) {
  return (
    <span className="sd-legend__item">
      <svg width="18" height="6" viewBox="0 0 18 6" aria-hidden="true"><line x1="0" y1="3" x2="18" y2="3" stroke="var(--slate)" strokeWidth="2" strokeDasharray="4 3" /></svg>
      {label}
    </span>
  );
}
function Empty({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="sd-empty">
      <div className="sd-empty__title">{label}</div>
      {hint ? <div className="sd-empty__hint">{hint}</div> : null}
    </div>
  );
}

/* ─── loading skeleton ────────────────────────────────────────────────────── */

function Skeleton() {
  return (
    <div className="sd-grid" aria-hidden="true">
      <div className="sd-kpis">
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="sd-card sd-skel sd-skel--kpi" />)}
      </div>
      <div className="sd-row sd-row--lf"><div className="sd-card sd-skel sd-skel--block" /><div className="sd-card sd-skel sd-skel--block" /></div>
      <div className="sd-row sd-row--even"><div className="sd-card sd-skel sd-skel--block" /><div className="sd-card sd-skel sd-skel--block" /></div>
      <div className="sd-row sd-row--jd"><div className="sd-card sd-skel sd-skel--block" /><div className="sd-card sd-skel sd-skel--block" /></div>
    </div>
  );
}
